import React, { useEffect, useState } from "react";
import { Modal, Row, Col, message } from "antd";
import { useFormik } from "formik";
import {
    CustomButton,
    FormInput,
    FormSelect,
    CustomTextArea,
    FormDatePicker,
    UploadWithTable
} from "../../../../../components";
import { EvaluationItem } from "./ModalManageItem";
import { InventoryViewAPI } from "../../../api/inventoryViewAPI";
import { UploadedFile } from "../../../../../utils";
import dayjs from "dayjs";

interface IModalAssessmentProps {
    isOpen: boolean;
    onClose: () => void;
    evaluation: EvaluationItem;
    onFiles: (files: UploadedFile[]) => void;
    files?: UploadedFile[];
    onSuccess: () => void;
}

const ModalAssessment: React.FC<IModalAssessmentProps> = ({
    isOpen,
    onClose,
    evaluation,
    files,
    onFiles,
    onSuccess
}) => {

    const [status, setStatus] = useState<{ id: number; status: string }[]>([]);

    const formik = useFormik({
        initialValues: {
            status_id: evaluation?.status_id || "",
            delivery_deadline: evaluation?.delivery_deadline || "",
            observations: evaluation?.observations || "",
        },
        onSubmit: async (values) => {
            try {
                const formData = new FormData();
                const formattedDate = values.delivery_deadline
                    ? dayjs(values.delivery_deadline).format("YYYY-MM-DD")
                    : "";
                formData.append("status_id", values.status_id);
                formData.append("delivery_deadline", formattedDate);
                formData.append("observations", values.observations);

                if (files && files.length > 0) {
                    files.forEach((uploadedFile) => {
                      if (uploadedFile.file instanceof File) {
                        formData.append("documents[]", uploadedFile.file);
                      }
                    });
                  }

                await InventoryViewAPI.evaluateConceptPetitionItems(
                    evaluation.codigo.toString(),
                    formData
                );

                message.success("Evaluación enviada correctamente");
                onClose(); 
                onSuccess();
            } catch (error) {
                console.error("Error al enviar la evaluación:", error);
                message.error("Ocurrió un error al evaluar el ítem");
            }
        },
    });

    useEffect(() => {
        const fetchData = async () => {
            try {
                const data = await InventoryViewAPI.fetchParameters(
                    ['status'], { status_model: 'ConceptItemEvaluationPetition' }
                );
                if (data) setStatus(data.status || []);
            } catch (error) {
                console.error('Error fetching parámetros:', error);
            }
        };
        fetchData();
    }, []);

    return (
        <Modal
            width='95vw'
            title="Evaluación técnica"
            open={isOpen}
            onCancel={onClose}
            footer={[
                <CustomButton key="cancel" label="Cancelar" onClick={onClose} buttonType="secondary" />,
                <CustomButton key="save" label="Guardar" onClick={() => formik.handleSubmit()} />,
            ]}
        >
            <form onSubmit={formik.handleSubmit}>
                <Row gutter={[16, 16]}>
                    <Col span={12}>
                        <FormInput
                            label="Rol del experto"
                            name="rolExperto"
                            placeholder="Seleccione"
                            value={evaluation.rol}
                            disabled
                        />
                    </Col>
                    <Col span={12}>
                        <FormInput
                            label="Nombre del experto"
                            name="nombreExperto"
                            value={evaluation.documento + " - " + evaluation.tecnico}
                            disabled
                        />
                    </Col>
                </Row>

                <Row gutter={[16, 16]}>
                    <Col span={12}>
                        <FormSelect
                            label="Estado a evaluar"
                            name="status_id"
                            placeholder="Seleccione"
                            value={formik.values.status_id}
                            onChange={(value) => formik.setFieldValue("status_id", value)}
                            onBlur={() => formik.setFieldTouched("status_id", true)}
                            options={status.map((x) => ({
                                name: x.status,
                                id: x.id.toString(),
                            }))}
                        />
                    </Col>
                    <Col span={12}>
                        <FormDatePicker
                            label="Plazo de entrega"
                            name="delivery_deadline"
                            placeholder="Seleccione una fecha"
                            value={formik.values.delivery_deadline}
                            onChange={(date) => formik.setFieldValue("delivery_deadline", date)}
                            onBlur={() => formik.setFieldTouched("delivery_deadline", true)}
                            disabledDate={(current) => current && current < new Date().setHours(0, 0, 0, 0)}
                        />
                    </Col>
                </Row>

                <Row gutter={[16, 16]} style={{ marginTop: "20px" }}>
                    <Col span={24}>
                        <CustomTextArea
                            label="Observaciones"
                            name="observations"
                            placeholder="Ingrese observaciones"
                            value={formik.values.observations}
                            onChange={formik.handleChange}
                            onBlur={formik.handleBlur}
                        />
                    </Col>
                </Row>

                {/* 📌 Subida de archivos con tabla */}
                <Row style={{ marginTop: "20px" }}>
                    <Col xs={24}>
                        <UploadWithTable onFilesChange={onFiles} files={files} />
                    </Col>
                </Row>
            </form>
        </Modal>
    );
};

export default ModalAssessment;